// Typed client for the publish/hosting endpoints (board-04 O8 backend).
// Visibility wire values are 'public' | 'unlisted' | 'private'; the UI's
// "Off" maps to 'private'.
import { apiDelete, apiGet, apiPatch, apiPost, ApiHttpError } from './api';
import { captchaHeader, requestPublishCaptchaToken } from './captcha';
import { lintMap, migrateMapDocument, type LintIssue, type MapDocument } from '../../engine';

export type Visibility = 'public' | 'unlisted' | 'private';

/**
 * Thrown when a publish is blocked because the project's map fails the
 * structural linter. `issues` carries the blocking errors; `message` is a
 * human-readable summary suitable for surfacing directly to the author.
 */
export class PublishBlockedError extends Error {
  readonly issues: LintIssue[];
  constructor(issues: LintIssue[]) {
    super(
      `Cannot publish: this map has ${issues.length} error${issues.length > 1 ? 's' : ''} that must be fixed first.\n` +
        issues.map((i) => `• ${i.message}`).join('\n'),
    );
    this.name = 'PublishBlockedError';
    this.issues = issues;
  }
}

/**
 * Fetch the project's saved map and block on any linter error (e.g. a Sunder
 * map missing a team spawn or a bomb site). The sim rejects such maps at load,
 * so we refuse to publish a map the game cannot run. Non-Sunder maps only trip
 * generic structural errors (unknown asset/floor); a map with no floors is
 * treated as un-lintable and allowed through.
 */
async function assertPublishable(projectId: string): Promise<void> {
  const mapData = await apiGet<MapDocument | null>(`/projects/${projectId}/map`);
  if (!mapData || !Array.isArray(mapData.floors)) return;
  const document = migrateMapDocument(mapData);
  const issues = lintMap(document, document.objectInstances ?? []);
  const errors = issues.filter((i) => i.severity === 'error');
  if (errors.length > 0) {
    throw new PublishBlockedError(errors);
  }
}

export interface Publication {
  slug: string;
  visibility: Visibility;
  version: number;
  versions: number;
  url: string;
  updated_at: string | null;
  /** Set when this publication redirects to its own dedicated site instead
   * of being rendered by the generic hosted-game template. */
  external_url: string | null;
}

export interface PublicationVersion {
  version: number;
  note: string | null;
  created_at: string | null;
  live: boolean;
}

// Publishing actions are captcha-gated by the API (Soulstack Verify, site key
// sk_studio_publish): when captcha is configured, the widget overlay collects a
// one-use token per call; in development the token is '' and no header is sent.
export async function publishProject(
  projectId: string,
  body: { slug?: string; visibility?: Visibility; note?: string; external_url?: string },
): Promise<Publication> {
  await assertPublishable(projectId);
  const token = await requestPublishCaptchaToken();
  return apiPost(`/projects/${projectId}/publish`, body, captchaHeader(token));
}

/**
 * 404s when the project has never been published; callers treat that as
 * null. A transient network/server failure rethrows instead - a live game
 * could otherwise misreport as "Not published" on a flaky request, with no
 * retry and nothing telling the creator the check itself failed.
 */
export async function getPublication(projectId: string): Promise<Publication | null> {
  try {
    return await apiGet<Publication>(`/projects/${projectId}/publication`);
  } catch (err) {
    if (err instanceof ApiHttpError && err.status === 404) return null;
    throw err;
  }
}

export async function updatePublication(
  projectId: string,
  body: { slug?: string; visibility?: Visibility; external_url?: string },
): Promise<Publication> {
  // Mirrors the api's require_captcha_for_publication_update: only a change to
  // external_url (where a published slug redirects to) redeems a token, so a
  // visibility toggle or slug rename does not ask the creator to solve anything.
  const token = body.external_url !== undefined ? await requestPublishCaptchaToken() : '';
  return apiPatch(`/projects/${projectId}/publication`, body, captchaHeader(token));
}

export function unpublish(projectId: string): Promise<{ status: string }> {
  return apiDelete(`/projects/${projectId}/publication`);
}

export function listPublicationVersions(projectId: string): Promise<PublicationVersion[]> {
  return apiGet(`/projects/${projectId}/publication/versions`);
}

/** Copies a published version's map back into the editable project map. */
export function restorePublicationVersion(
  projectId: string,
  version: number,
): Promise<{ status: string; version: number }> {
  return apiPost(`/projects/${projectId}/publication/versions/${version}/restore`);
}
